import { Select } from '@radix-ui/themes'
import { add, format, getDate, getDaysInMonth, getISODay, parseISO } from 'date-fns'
import { useCallback, useEffect, useState } from 'react'
import { convertDateToIsoDate } from './utils'

const enum RecurOptions {
  NEVER = 'NEVER',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY_DATE = 'MONTHLY_DATE',
  MONTHLY_DAY = 'MONTHLY_DAY',
}

function getNthDayOfMonth(date: Date): [number, boolean] {
  const dayOfMonth = getDate(date)
  return [Math.ceil(dayOfMonth / 7), getDaysInMonth(date) - dayOfMonth < 7]
}

// This only has to work for 1-5
function weekOrdinal(n: number, isLast: boolean): string {
  switch (n) {
    case 1:
      return 'first'
    case 2:
      return 'second'
    case 3:
      return 'third'
    default:
      return isLast ? 'last' : `fourth`
  }
}

function getDatesUntil(
  startDate: string,
  endDate: string,
  incrementorFn: (currentDate: string) => string,
): string[] {
  const dates: string[] = []
  let currentDate = startDate
  while (currentDate <= endDate) {
    dates.push(currentDate)
    currentDate = incrementorFn(currentDate)
  }

  return dates
}

export const ReoccurringEventSelector = ({
  startDate,
  endDate,
  onDatesChanged,
}: {
  startDate?: string
  endDate?: string
  onDatesChanged: (dates: string[]) => void
}) => {
  const startDateAsDate = parseISO(startDate || convertDateToIsoDate(new Date()))
  const dayOfMonth = format(startDateAsDate, 'do')
  const dayOfWeek = format(startDateAsDate, 'eeee')
  const [nthDayOfMonth, isLastNthOfMonth] = getNthDayOfMonth(startDateAsDate)
  const [selectedRepeat, setSelectedRepeat] = useState<string>(RecurOptions.NEVER)

  const onValueChange = useCallback(
    (value: string) => {
      setSelectedRepeat(value)

      if (!startDate) {
        onDatesChanged([])
        return
      }

      if (!endDate) {
        if (value === RecurOptions.NEVER) {
          onDatesChanged([startDate])
        } else {
          onDatesChanged([])
        }
        return
      }

      switch (value) {
        case RecurOptions.NEVER:
          onDatesChanged([startDate])
          break
        case RecurOptions.DAILY:
          onDatesChanged(
            getDatesUntil(startDate, endDate, (currentDate) => {
              const parsed = parseISO(currentDate)
              return convertDateToIsoDate(add(parsed, { days: 1 }))
            }),
          )
          break
        case RecurOptions.WEEKLY:
          onDatesChanged(
            getDatesUntil(startDate, endDate, (currentDate) => {
              const parsed = parseISO(currentDate)
              return convertDateToIsoDate(add(parsed, { weeks: 1 }))
            }),
          )
          break
        case RecurOptions.MONTHLY_DATE:
          onDatesChanged(
            getDatesUntil(startDate, endDate, (currentDate) => {
              const parsed = parseISO(currentDate)
              return convertDateToIsoDate(add(parsed, { months: 1 }))
            }),
          )
          break
        case RecurOptions.MONTHLY_DAY:
          onDatesChanged(
            getDatesUntil(startDate, endDate, (currentDate) => {
              const parsed = parseISO(currentDate)
              let nextDate = add(parsed, { months: 1 })
              nextDate.setDate(1)
              while (getISODay(nextDate) !== getISODay(parsed)) {
                nextDate = add(nextDate, { days: 1 })
              }

              // nextDate is now the first of the needed day of the week for the next month
              // if there are 4 or more days of that date remaining that means there are 5 total in the month
              const areThere5 = (getDaysInMonth(nextDate) - getDate(nextDate)) / 7 >= 4
              let targetWeek = nthDayOfMonth - 1
              if (isLastNthOfMonth) {
                targetWeek = areThere5 ? 4 : 3
              }

              return convertDateToIsoDate(add(nextDate, { weeks: targetWeek }))
            }),
          )
          break
      }
    },
    [endDate, isLastNthOfMonth, nthDayOfMonth, onDatesChanged, startDate],
  )

  useEffect(() => {
    onValueChange(selectedRepeat)
  }, [onValueChange, selectedRepeat, startDate])

  return (
    <Select.Root
      value={selectedRepeat}
      onValueChange={onValueChange}
      defaultValue={RecurOptions.NEVER}>
      <Select.Trigger />
      <Select.Content>
        <Select.Item value={RecurOptions.NEVER}>Never</Select.Item>
        <Select.Item value={RecurOptions.DAILY}>Daily</Select.Item>
        <Select.Item value={RecurOptions.WEEKLY}>Weekly on {dayOfWeek}</Select.Item>
        <Select.Item value={RecurOptions.MONTHLY_DATE}>Monthly on the {dayOfMonth}</Select.Item>
        <Select.Item value={RecurOptions.MONTHLY_DAY}>
          Monthly on the {weekOrdinal(nthDayOfMonth, isLastNthOfMonth)} {dayOfWeek}
        </Select.Item>
      </Select.Content>
    </Select.Root>
  )
}
